import React, { useCallback, useState } from "react";

import { Link as RRLink } from "react-router-dom";
import { useForm } from "react-hook-form-7";

import {
  Alert,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Link,
  Stack,
  Divider,
  Text,
} from "@chakra-ui/react";

import useTranslations from "../../../hooks/useTranslations";

import LoginLayout from "../layout/LoginLayout";

import client from "../../../apolloClient";
import {
  GetPublicUserDocument,
  PublicUser,
} from "../../../graphql/generated/definitions";

type SAMLFormInputs = { email: string };

export default function SAMLPage() {
  const t = useTranslations();

  const [hasSAMLError, setSAMLError] = useState(false);

  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm<SAMLFormInputs>({ mode: "onChange" });

  const onSubmit = useCallback(async ({ email }) => {
    try {
      const publicUserResult = await client.query({
        query: GetPublicUserDocument,
        variables: { email },
      });

      const { samlRedirectUrl } = publicUserResult.data
        .publicUser as PublicUser;

      if (!samlRedirectUrl) {
        setSAMLError(true);
      } else {
        window.location.replace(samlRedirectUrl);
      }
    } catch (e) {
      setSAMLError(true);
    }
  }, []);

  return (
    <LoginLayout title={t("saml.connectWithSAML") as string}>
      <Stack spacing="6">
        <Divider color="gray.200" />
        <Text fontWeight="semibold">{t("saml.enterProEmail")}</Text>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing="5">
            <FormControl
              id="email"
              isRequired
              isInvalid={!!errors?.email?.message}
            >
              <FormLabel>{t("forms.fields.email")}</FormLabel>
              <Input
                size="lg"
                type="email"
                placeholder={t("saml.proEmailPlaceholder") as string}
                // This looks rather ugly but native form validation block the one made by RHF
                isRequired={false}
                aria-required="true"
                {...register("email", {
                  required: t("forms.errors.required") as string,
                })}
              />
              <FormErrorMessage>{errors?.email?.message}</FormErrorMessage>
            </FormControl>
            {hasSAMLError && (
              <Alert status="warning" id="login-form-error">
                {t("saml.authError")}
              </Alert>
            )}

            <Stack spacing="6" pt="4">
              <Button
                disabled={!isValid}
                type="submit"
                isFullWidth
                colorScheme="primary"
              >
                {t("saml.connectWithSSO")}
              </Button>
              <Link
                as={RRLink}
                to="/login"
                color="secondary"
                textAlign="center"
                fontSize="sm"
                fontWeight="semibold"
              >
                {t("login.backToLogin")}
              </Link>
            </Stack>
          </Stack>
        </form>
      </Stack>
    </LoginLayout>
  );
}
